// Cuerpo de las páginas legales públicas (/terms, /privacy).
// Reusa la fuente única de @shared/legal para no divergir del texto in-app.
// Muestra una versión COMBINADA (Usuarios + Profesionales) bajo una sola URL
// pública, requerida por App Store / Play Store. El chrome (navbar + footer) lo
// aporta PageLayout; acá solo va el contenido del documento.

import type { LegalDocument } from '@shared/legal/types';
import {
  TERMS_CLIENT_DOCUMENT,
  TERMS_PROFESSIONAL_DOCUMENT,
} from '@shared/legal/terms';
import {
  PRIVACY_CLIENT_DOCUMENT,
  PRIVACY_PROFESSIONAL_DOCUMENT,
} from '@shared/legal/privacy';
import { strings } from '@/data/strings';
import styles from './LegalPage.module.css';

export type LegalSlug = 'terms' | 'privacy';

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  const month = strings.legal.months[parseInt(m, 10) - 1] ?? m;
  return `${parseInt(d, 10)} de ${month} de ${y}`;
}

interface RoleDoc {
  label: string;
  doc: LegalDocument;
}

interface PageConfig {
  title: string;
  roleDocs: RoleDoc[];
}

function getConfig(slug: LegalSlug): PageConfig {
  switch (slug) {
    case 'terms':
      return {
        title: strings.legal.terms.title,
        roleDocs: [
          { label: strings.legal.roleClient, doc: TERMS_CLIENT_DOCUMENT },
          { label: strings.legal.roleProfessional, doc: TERMS_PROFESSIONAL_DOCUMENT },
        ],
      };
    case 'privacy':
      return {
        title: strings.legal.privacy.title,
        roleDocs: [
          { label: strings.legal.roleClient, doc: PRIVACY_CLIENT_DOCUMENT },
          { label: strings.legal.roleProfessional, doc: PRIVACY_PROFESSIONAL_DOCUMENT },
        ],
      };
  }
}

export function LegalPage({ slug }: { slug: LegalSlug }) {
  const config = getConfig(slug);
  const updatedAt = config.roleDocs[0].doc.updatedAt;
  const combined = config.roleDocs.length > 1;

  return (
    <div className={styles.wrap}>
      <h1 className={styles.title}>{config.title}</h1>
      <p className={styles.updated}>
        {strings.legal.updatedPrefix} {formatDate(updatedAt)}
      </p>

      {config.roleDocs.map((roleDoc) => (
        <section key={roleDoc.label} className={styles.roleSection}>
          {combined && <h2 className={styles.roleTitle}>{roleDoc.label}</h2>}
          {roleDoc.doc.sections.map((section) => (
            <article key={section.heading} className={styles.section}>
              <h3 className={styles.heading}>{section.heading}</h3>
              <p className={styles.body}>{section.body}</p>
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}
